import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { BoFormsModule } from '@bo/ng-forms';
import { FieldComponent } from '../form-field/form-field.component';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CountryISO, NgxIntlTelInputModule, PhoneNumberFormat, SearchCountryField } from 'ngx-intl-tel-input';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';

interface FieldValue {
  number: string;
  internationalNumber: string;
  nationalNumber: string;
  e164Number: string;
  countryCode: string;
  dialCode: string;
}

@Component({
  selector: 'app-phone-field',
  imports: [BoFormsModule, NgxIntlTelInputModule, BsDropdownModule, FormsModule, ReactiveFormsModule],
  templateUrl: './phone-field.component.html',
  styleUrl: './phone-field.component.scss',
})
export class PhoneFieldComponent extends FieldComponent implements OnInit, AfterViewInit, OnDestroy {
  // Country dropdown configuration
  SearchCountryField = SearchCountryField;
  preferredCountries: CountryISO[] = [CountryISO.SouthAfrica, CountryISO.UnitedStates, CountryISO.UnitedKingdom];

  // Internal control to bind to the library (prevents object from reaching parent)
  // Can be either a string (E.164 from backend) or FieldValue object (from ngx-intl-tel-input)
  internalControl = new FormControl<string | FieldValue | null>('');

  private isInitializing = false;
  private initialE164Value: string | null = null;

  override ngOnInit() {
    super.ngOnInit();

    // Subscribe to internal control changes and sync E.164 format to parent
    this.subscriptions.add(
      this.internalControl.valueChanges.subscribe((value: string | FieldValue | null) => {
        if (this.isInitializing) {
          return; // Skip during initialisation to avoid loops
        }

        if (value && typeof value === 'object' && 'e164Number' in value) {
          // ngx-intl-tel-input emits an object with e164Number property
          const e164Value = value.e164Number || '';

          // Update parent control with E.164 format - emit event so parent valueChanges fires
          if (this.fieldControl?.value !== e164Value) {
            this.fieldControl?.setValue(e164Value);
          }
        } else if (!value) {
          // Cleared input — keep parent in sync so required validation works
          if (this.fieldControl?.value) {
            this.fieldControl.setValue('');
          }
        }
      }),
    );

    // Blur marks the library control touched, not the parent form control.
    // Sync touched/dirty so validityClass + bo-form-field-errors work like other fields.
    this.subscriptions.add(
      this.internalControl.events.subscribe(() => {
        if (this.internalControl.touched && !this.fieldControl?.touched) {
          this.fieldControl?.markAsTouched();
        }
        if (this.internalControl.dirty && !this.fieldControl?.dirty) {
          this.fieldControl?.markAsDirty();
        }
      }),
    );

    // Store initial value to be processed after view init
    if (this.fieldControl?.value) {
      this.initialE164Value = this.fieldControl.value;
    }
  }

  override ngAfterViewInit() {
    super.ngAfterViewInit();

    // Process initial E.164 value after view is fully initialized
    if (this.initialE164Value) {
      this.isInitializing = true;

      // First, set the E.164 value so the library can parse it
      this.internalControl.setValue(this.initialE164Value, { emitEvent: false });

      // Wait for the library to process and then re-patch with national number only
      setTimeout(() => {
        const currentValue = this.internalControl.value;
        if (this.isFieldValue(currentValue)) {
          // Re-patch with just the number (without dial code) for separateDialCode mode
          this.internalControl.setValue(currentValue.number, { emitEvent: false });
        }
        this.isInitializing = false;
        this.initialE164Value = null;
      }, 100); // Increased timeout to ensure library has processed the value
    } else {
      // No initial value, make sure we're not stuck in initializing state
      this.isInitializing = false;
    }
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this.subscriptions.unsubscribe();
  }

  /**
   * Type guard to check if value is a FieldValue object
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private isFieldValue(value: any): value is FieldValue {
    return value && typeof value === 'object' && 'number' in value && typeof value.number === 'string';
  }

  protected readonly PhoneNumberFormat = PhoneNumberFormat;
}
